import { useCallback, useEffect, useMemo, useState } from 'react';
import { array, bool, boolean, number, object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { getCommonCodes } from '../../../../../apis/admin/CommonCode';
import { CommonCode } from '../../../../../models/admin/CommonCode';

const useMemberRegist = () => {
  const { t } = useTranslation();
  const [pwdBlackList, setPwdBlackList] = useState<CommonCode[]>([]);
  const [targetWord, setTargetWord] = useState<string>('');
  const getDmstFrgDivCds = useCallback(async () => {
    await getCommonCodes('PWD_BLACK_LIST').then((result: CommonCode[]) => {
      return setPwdBlackList(result);
    });
  }, []);

  useEffect(() => {
    getDmstFrgDivCds();
  }, []);

  const SPECIAL_REGEX = /[!@#$%^&*()_=+[\]{};:'",.<>/?-]/; // 허용 특수문자(정책에 맞게 조정)
  const ALLOWED_CHARS_REGEX = /^[a-zA-Z0-9!@#$%^&*()_=+[\]{};:'",.<>/?-]*$/; // 허용 문자만
  const NO_TRIPLE_SAME_CHAR_REGEX = /^(?!.*(.)\1{2}).*$/; // 동일문자 3연속 금지

  //   vdcpCd: '', //공급업체코드_로그인ID
  //     dmstFrgDivsCd: 'INTERNAL', //국내해외구분
  //     vdcpMgrNm: '', //이름
  //     soprMgrCopCd: '', //엔솔법인코드
  //     soprMgrNm: '', //엔솔담당자
  //     vdcpNm: '', //업체명_직접입력
  //     vdcpErpNm: '', //업체명_ERP
  //     pwd: '', //패스워드
  //     vdcpMgrPhn: '', //전화번호
  //     vdcpMgrEmal: '', //이메일
  //     idvlIfoClctCncCd: 'N', //개인정보의 수집 및 이용에 대한안내 동의
  //     dlvPayAgmnCncCd: 'N', //납품대금지급에관한약정서 동의
  //     chtrIdnClctCncCd: 'N', //고유식별정보와 수집 및 이용동의
  //     crudKey: CrudCode.CREATE,

  // 비밀번호 블랙리스트 체크
  const findBlackListWord = useCallback(
    (pw: string) => {
      const lowerPw = (pw || '').toLowerCase();

      return pwdBlackList
        .map((v) => v.cmnCd?.toLowerCase() || '')
        .find((word) => word && lowerPw.includes(word));
    },
    [pwdBlackList]
  );

  const krRegSchema = useMemo(() => {
    return object({
      vdcpMgrNm: string().required(
        t('com.label.00516', '__회원 가입자 이름은 필수 입력 항목입니다.') as string
      ),
      vdcpCd: string().required(
        t('com.label.00405', '__Vendor ID는 필수 입력 항목입니다.') as string
      ),
      cfrmVdcpCd: boolean().isTrue(
        t('com.label.00559', '__Vendor ID 확인이 필요합니다.') as string
      ),
      vdcpNm: string().required(t('com.label.00518', '__업체명 필수 입력 항목입니다.') as string),
      vdcpMgrPhn: string().required(
        t('com.label.00521', '__전화번호 필수 입력 항목입니다.') as string
      ),
      vdcpMgrEmal: string().required(
        t('com.label.00597', '__이메일 필수 입력 항목입니다.') as string
      ),
      soprMgrCopCd: string().required(
        t('com.label.00507', '__법인코드는 필수 입력 항목입니다.') as string
      ),
      soprMgrNm: string().test(
        'soprMgrNm',
        t('com.label.00508', '__담당자 필수 입력 항목입니다.') as string,
        (value) => {
          if (value) {
            return true;
          } else {
            return false;
          }
        }
      ),
      // 영업부서 담당자 이름, 전화번호, 이메일 필수
      vdcpMgrNm1: string().required(
        t('com.label.00522', '__영업부서 담당자 이름은 필수 입력 항목입니다.') as string
      ),
      vdcpMgrEncPhn: string().required(
        t('com.label.00523', '__영업부서 담당자 전화번호 필수 입력 항목입니다.') as string
      ),
      vdcpMgrEmal1: string().required(
        t('com.label.00524', '__영업부서 담당자 이메일 필수 입력 항목입니다.') as string
      ),

      // 자금부서 담당자 이름, 전화번호, 이메일 필수
      vdcpFundMgrNm: string().required(
        t('com.label.00525', '__자금부서 담당자 이름은 필수 입력 항목입니다.') as string
      ),
      vdcpFundMgrEncPhn: string().required(
        t('com.label.00526', '__자금부서 담당자 전화번호 필수 입력 항목입니다.') as string
      ),
      vdcpFundMgrEmal: string().required(
        t('com.label.00527', '__자금부서 담당자 이메일 필수 입력 항목입니다.') as string
      ),

      // 송금 확인 담당자 영역 - 송금 담당자 이름, 전화번호, 이메일 나중에 필수 항목으로 되면 주석 풀고 사용
      // vdcpRmitMgrNm: string().required(
      //   t(
      //     'com.label.mgt.required.vdcpRmitMgrNm',
      //     '__송금 확인 담당자 이름은 필수 입력 항목입니다.'
      //   ) as string
      // ),
      // vdcpRmitMgrEncPhn: string().required(
      //   t(
      //     'com.label.mgt.required.vdcpRmitMgrEncPhn',
      //     '__송금 확인 담당자 전화번호 필수 입력 항목입니다.'
      //   ) as string
      // ),
      // vdcpRmitMgrEmal: string().required(
      //   t(
      //     'com.label.mgt.required.vdcpRmitMgrEmal',
      //     '__송금 확인 담당자 이메일 필수 입력 항목입니다.'
      //   ) as string
      // ),

      // 개인정보 수집 동의
      idvlIfoClctCncCd: string().test(
        'idvlIfoClctCncCd',
        t('com.label.00528', '__개인정보 수집 안내에 동의해주세요.') as string,
        (value, ctx) => {
          if (value == 'N') {
            return false;
          }
          return true;
        }
      ),
      dlvPayAgmnCncCd: string().test(
        'dlvPayAgmnCncCd',
        t('com.label.00529', '__납품대금 지급에 관한 약정서에 동의해주세요.') as string,
        (value, ctx) => {
          if (value == 'N') {
            return false;
          }
          return true;
        }
      ),
      chtrIdnClctCncCd: string().test(
        'chtrIdnClctCncCd',
        t('com.label.00530', '__고유식별정보의 수집 및 이용에 동의해주세요.') as string,
        (value, ctx) => {
          if (value == 'N') {
            return false;
          }
          return true;
        }
      ),
    });
  }, []);

  const ovRegSchema = useMemo(() => {
    return object({
      vdcpMgrNm: string().required(
        t('com.label.00516', '__회원 가입자 이름은 필수 입력 항목입니다.') as string
      ),
      vdcpCd: string().required(
        t('com.label.00405', '__Vendor ID는 필수 입력 항목입니다.') as string
      ),
      cfrmVdcpCd: boolean().isTrue(
        t('com.label.00559', '__Vendor ID 확인이 필요합니다.') as string
      ),
      vdcpNm: string().required(t('com.label.00518', '__업체명 필수 입력 항목입니다.') as string),
      vdcpMgrPhn: string().required(
        t('com.label.00521', '__전화번호 필수 입력 항목입니다.') as string
      ),
      vdcpMgrEmal: string().required(
        t('com.label.00597', '__이메일 필수 입력 항목입니다.') as string
      ),
      pwd: string()
        .required(t('com.label.00519', '__패스워드 필수 입력 항목입니다.') as string)
        .max(
          15,
          t(
            'com.label.pwd.비밀번호는 최대 15자까지 가능합니다.',
            '__비밀번호는 최대 15자까지 가능합니다.'
          ) as string
        )
        .test('pw-policy', '비밀번호 정책을 만족하지 않습니다.', function (value) {
          const pw = value ?? '';

          // 1) 허용 문자 체크
          if (!ALLOWED_CHARS_REGEX.test(pw)) {
            return this.createError({
              message: t(
                'com.label.pwd.비밀번호는 영문/숫자/특수문자만 사용할 수 있습니다.',
                '__비밀번호는 영문/숫자/특수문자만 사용할 수 있습니다.'
              ) as string,
            });
          }

          // 2) 동일문자 3연속 금지
          if (!NO_TRIPLE_SAME_CHAR_REGEX.test(pw)) {
            return this.createError({
              message: t(
                'com.label.pwd.동일한 문자를 3자리 이상 연속 사용할 수 없습니다.',
                '__동일한 문자를 3자리 이상 연속 사용할 수 없습니다.'
              ) as string,
            });
          }

          // 3) 조합 판별 (영문/숫자/특수 중 2종 이상)
          const hasLetter = /[a-zA-Z]/.test(pw);
          const hasNumber = /[0-9]/.test(pw);
          const hasSpecial = SPECIAL_REGEX.test(pw);

          const typeCount = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;

          if (typeCount < 3) {
            return this.createError({
              message: t(
                'com.label.pwd.영문/숫자/특수문자 3종류를 포함해야 합니다.',
                '__영문/숫자/특수문자 3종류를 포함해야 합니다.'
              ) as string,
            });
          }

          // 4) 길이 규칙
          // - 3종류: 8자 이상
          // - 2종류: 10자 이상
          if (typeCount === 3 && pw.length < 8) {
            return this.createError({
              message: t(
                'com.label.pwd.패스워드는 숫자/영문/특수문자 3종류로 구성되고 8자리 이상이어야 합니다.',
                '__패스워드는 숫자/영문/특수문자 3종류로 구성되고 8자리 이상이어야 합니다.'
              ) as string,
            });
          }

          // if (typeCount === 2 && pw.length < 10) {
          //   return this.createError({
          //     message: t(
          //       'com.label.pwd.2종류 조합 시 비밀번호는 10자 이상이어야 합니다.',
          //       '__2종류 조합 시 비밀번호는 10자 이상이어야 합니다.'
          //     ) as string,
          //   });
          // }

          // 아이디 비교
          const { userId } = this.parent as { userId?: string };
          const { vdcpCd } = this.parent as { vdcpCd?: string };
          const targetId = vdcpCd ?? userId;

          if (targetId && pw.toLowerCase().includes(targetId.toLowerCase()))
            return this.createError({
              message: t(
                'com.label.pwd.벤더 아이디가 비밀번호에 포함될 수 없습니다.',
                '__벤더 아이디가 비밀번호에 포함될 수 없습니다.'
              ) as string,
            });

          const matchedWord = findBlackListWord(value);

          if (matchedWord)
            return this.createError({
              message: `'${matchedWord}' ${
                t(
                  'com.label.pwd.단어를 포함하는 비밀번호는 사용할 수 없습니다.',
                  '__단어를 포함하는 비밀번호는 사용할 수 없습니다.'
                ) as string
              }`,
            });

          return true;
        }),
      cfrmPwd: string().required(
        t('com.label.00520', '__패스워드확인 필수 입력 항목입니다.') as string
      ),
      cfrmPwdAll: boolean().isTrue(
        t('com.label.00656', '__패스워드가 일치하지 않습니다.') as string
      ),
      soprMgrCopCd: string().required(
        t('com.label.00507', '__법인코드는 필수 입력 항목입니다.') as string
      ),
      soprMgrNm: string().test(
        'soprMgrNm',
        t('com.label.00508', '__담당자 필수 입력 항목입니다.') as string,
        (value) => {
          if (value) {
            return true;
          } else {
            return false;
          }
        }
      ),
      // 영업부서 담당자 이름, 전화번호, 이메일 필수
      vdcpMgrNm1: string().required(
        t('com.label.00522', '__영업부서 담당자 이름은 필수 입력 항목입니다.') as string
      ),
      vdcpMgrEncPhn: string().required(
        t('com.label.00523', '__영업부서 담당자 전화번호 필수 입력 항목입니다.') as string
      ),
      vdcpMgrEmal1: string().required(
        t('com.label.00524', '__영업부서 담당자 이메일 필수 입력 항목입니다.') as string
      ),

      // 자금부서 담당자 이름, 전화번호, 이메일 필수
      vdcpFundMgrNm: string().required(
        t('com.label.00525', '__자금부서 담당자 이름은 필수 입력 항목입니다.') as string
      ),
      vdcpFundMgrEncPhn: string().required(
        t('com.label.00526', '__자금부서 담당자 전화번호 필수 입력 항목입니다.') as string
      ),
      vdcpFundMgrEmal: string().required(
        t('com.label.00527', '__자금부서 담당자 이메일 필수 입력 항목입니다.') as string
      ),

      // 송금 확인 담당자 영역 - 송금 담당자 이름, 전화번호, 이메일 나중에 필수 항목으로 되면 주석 풀고 사용
      // vdcpRmitMgrNm: string().required(
      //   t(
      //     'com.label.mgt.required.vdcpRmitMgrNm',
      //     '__송금 확인 담당자 이름은 필수 입력 항목입니다.'
      //   ) as string
      // ),
      // vdcpRmitMgrEncPhn: string().required(
      //   t(
      //     'com.label.mgt.required.vdcpRmitMgrEncPhn',
      //     '__송금 확인 담당자 전화번호 필수 입력 항목입니다.'
      //   ) as string
      // ),
      // vdcpRmitMgrEmal: string().required(
      //   t(
      //     'com.label.mgt.required.vdcpRmitMgrEmal',
      //     '__송금 확인 담당자 이메일 필수 입력 항목입니다.'
      //   ) as string
      // ),

      // 개인정보 수집 동의
      idvlIfoClctCncCd: string().test(
        'idvlIfoClctCncCd',
        t('com.label.00528', '__개인정보 수집 안내에 동의해주세요.') as string,
        (value, ctx) => {
          if (value == 'N') {
            return false;
          }
          return true;
        }
      ),
      dlvPayAgmnCncCd: string().test(
        'dlvPayAgmnCncCd',
        t('com.label.00529', '__납품대금 지급에 관한 약정서에 동의해주세요.') as string,
        (value, ctx) => {
          if (value == 'N') {
            return false;
          }
          return true;
        }
      ),
      chtrIdnClctCncCd: string().test(
        'chtrIdnClctCncCd',
        t('com.label.00530', '__고유식별정보의 수집 및 이용에 동의해주세요.') as string,
        (value, ctx) => {
          if (value == 'N') {
            return false;
          }
          return true;
        }
      ),
    });
  }, [pwdBlackList]);

  return {
    krRegSchema,
    ovRegSchema,
  };
};

export default useMemberRegist;
